/**
 * TikTok Create Product 表单字数 / 数量上限（UI maxLength 与本地校验共用）。
 * 文档：client/docs/tiktok/API reference/products/Create Product.md
 */

/** description：Max length 10,000 */
export const TIKTOK_DESCRIPTION_MAX_CHARS = 10000;

/** external_product_id：Max length 999 */
export const TIKTOK_EXTERNAL_PRODUCT_ID_MAX = 999;

/** seller_sku：Valid length 1–50；Text without spaces */
export const TIKTOK_SELLER_SKU_MAX_LEN = 50;

/** sales_attributes[].value_name：Max length 50 */
export const TIKTOK_SALES_ATTR_VALUE_NAME_MAX = 50;

/** sales_attributes[].name：Max length 20 */
export const TIKTOK_SALES_ATTR_NAME_MAX = 20;

/** product_attributes[].values[].name：Max length 2000 */
export const TIKTOK_PRODUCT_ATTR_VALUE_NAME_MAX = 2000;

/** inventory.quantity：Create Product 官方 Valid range [1, 99,999]（不可为 0） */
export const TIKTOK_INVENTORY_QTY_MIN = 1;
export const TIKTOK_INVENTORY_QTY_MAX = 99999;

/** US list_price 上限 */
export const TIKTOK_US_LIST_PRICE_MAX = 7600;

/** Create Product title 长度：min=1 的市场 */
const TIKTOK_TITLE_MIN1_REGIONS = new Set([
  'BR', 'MX', 'DE', 'ES', 'FR', 'IE', 'IT', 'JP', 'GB', 'UK', 'US',
]);

function normalizeMarketRegion(raw) {
  const code = String(raw || '').trim().toUpperCase();
  return code === 'UK' ? 'GB' : code;
}

/**
 * TikTok Shop「欧盟/英国」发品合规实体（Create Manufacturer / Responsible Person）。
 * US/SEA/JP/BR/MX 等不展示、不提交这两项。
 * 含欧盟 27 国 ISO 二字码，以及英国 GB（UK 归一为 GB）。
 */
const TIKTOK_EU_MARKET_REGIONS = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU',
  'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  'GB',
]);

/** 店铺/市场是否走欧盟/英国发品合规实体（制造商 + 责任人，均为选填） */
export function isTiktokEuMarketRegion(region) {
  const code = normalizeMarketRegion(region);
  return Boolean(code) && TIKTOK_EU_MARKET_REGIONS.has(code);
}

/**
 * Create Product title 长度：
 * - DE/ES/FR/IE/IT/JP/UK/US: [1, 255]
 * - BR/MX: [1, 300]
 * - Other: [25, 255]
 * titleMaxLength（publish/standard）优先作上限。
 */
export function resolveTiktokTitleLengthBounds(region, titleMaxLength = 0) {
  const code = normalizeMarketRegion(region);
  const maxFromMeta = Number(titleMaxLength);
  const max = maxFromMeta > 0
    ? maxFromMeta
    : (code === 'BR' || code === 'MX' ? 300 : 255);
  // 市场未知时放宽下限，避免未加载 region 误拦
  const min = !code || TIKTOK_TITLE_MIN1_REGIONS.has(code) || code === 'GB' ? 1 : 25;
  return { min, max };
}

/** 默认标题上限（市场未知时）：255 */
export const TIKTOK_TITLE_MAX_CHARS_DEFAULT = 255;

// ---------------------------------------------------------------------------
// Create Product 补充字段（预约上架 / 搜索词 / 卖点 / 限购 / 预售 / 幂等键）
// 约束一律取自本地 doc：client/docs/tiktok/API reference/products/Create Product.md
// ---------------------------------------------------------------------------

/** search_terms：官方 ≤15 条、合计 ≤250 字符（Create Product.md:146） */
export const TIKTOK_SEARCH_TERMS_MAX = 15;
export const TIKTOK_SEARCH_TERMS_MAX_CHARS = 250;

/** key_product_features：官方 ≤5 条、合计 ≤1500 字符（Create Product.md:147） */
export const TIKTOK_KEY_FEATURES_MAX = 5;
export const TIKTOK_KEY_FEATURES_MAX_CHARS = 1500;

/** scheduled_sale.schedule_sale_time：须晚于当前、且 ≤90 天（Create Product.md:143） */
export const TIKTOK_SCHEDULED_SALE_MAX_DAYS = 90;

/** idempotency_key：店内唯一、≤128、建议 v4 UUID（Create Product.md:139） */
export const TIKTOK_IDEMPOTENCY_KEY_MAX = 128;

/** pre_sale：仅当 Get Category Rules 的 allowed_special_product_types 非空（Create Product.md:73） */
export const TIKTOK_PRESALE_PRE_ORDER = 'PRE_ORDER';
export const TIKTOK_PRESALE_MADE_TO_ORDER = 'MADE_TO_ORDER';
export const TIKTOK_PRESALE_CUSTOM = 'CUSTOM';

const TIKTOK_PRESALE_TYPES_US = new Set([
  TIKTOK_PRESALE_PRE_ORDER,
  TIKTOK_PRESALE_MADE_TO_ORDER,
  TIKTOK_PRESALE_CUSTOM,
]);
/** UK / EU / SEA / JP / LATAM：文档仅列 PRE_ORDER */
const TIKTOK_PRESALE_TYPES_OTHER = new Set([TIKTOK_PRESALE_PRE_ORDER]);

/** 该市场支持的预售类型（US 三种；其余仅 PRE_ORDER） */
export function resolveTiktokPreSaleTypes(region) {
  const code = normalizeMarketRegion(region);
  return [...(code === 'US' ? TIKTOK_PRESALE_TYPES_US : TIKTOK_PRESALE_TYPES_OTHER)];
}

/**
 * 该市场 + 类型的履约方式与时间窗；不支持的类型返回 null。
 * - US PRE_ORDER：release_date，当前起 3–60 天（Create Product.md:77）
 * - US MADE_TO_ORDER：handling_duration_days 3–14 工作日（:76）
 * - US CUSTOM：handling_duration_days 3–30 工作日（:76）
 * - 非 US PRE_ORDER：handling_duration_days 3–30 日历日（:76）
 * @returns {null | { type: string, fulfillment: 'release_date'|'handling_duration_days',
 *   minDays: number, maxDays: number, unit: 'CALENDAR_DAY'|'BUSINESS_DAY' }}
 */
export function resolveTiktokPreSaleRule(region, type) {
  const code = normalizeMarketRegion(region);
  const normalized = String(type ?? '').trim().toUpperCase();
  if (!normalized || !resolveTiktokPreSaleTypes(code).includes(normalized)) return null;
  if (code === 'US') {
    if (normalized === TIKTOK_PRESALE_PRE_ORDER) {
      return {
        type: normalized,
        fulfillment: 'release_date',
        minDays: 3,
        maxDays: 60,
        unit: 'CALENDAR_DAY',
      };
    }
    return {
      type: normalized,
      fulfillment: 'handling_duration_days',
      minDays: 3,
      maxDays: normalized === TIKTOK_PRESALE_MADE_TO_ORDER ? 14 : 30,
      unit: 'BUSINESS_DAY',
    };
  }
  return {
    type: normalized,
    fulfillment: 'handling_duration_days',
    minDays: 3,
    maxDays: 30,
    unit: 'CALENDAR_DAY',
  };
}

/** 日期 / datetime-local / 秒 / 毫秒 → Unix 毫秒；非法返回 0。 */
function toUnixMillis(raw) {
  if (raw == null || raw === '') return 0;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return Math.trunc(raw > 1e12 ? raw : raw * 1000);
  }
  const text = String(raw).trim();
  if (!text) return 0;
  if (/^\d+$/.test(text)) return toUnixMillis(Number(text));
  // datetime-local（YYYY-MM-DDTHH:mm）与 YYYY-MM-DD 均按本地时区解析；
  // 日期选择组件回写为「YYYY-MM-DD HH:mm:ss」，先把空格换成 T，避免个别内核 Date.parse 失败。
  const parsed = Date.parse(text.replace(' ', 'T'));
  return Number.isFinite(parsed) ? parsed : 0;
}

/** 预约上架时间 → Unix 毫秒（0 = 未开启 / 非法） */
export function resolveTiktokScheduledSaleMillis(raw) {
  return toUnixMillis(raw);
}

/** 距 `now` 的天数（可为小数）；无法解析返回 NaN。用于预约 / 预售日期窗口判定。 */
export function tiktokDaysFromNow(raw, now = Date.now()) {
  const ms = toUnixMillis(raw);
  if (!(ms > 0)) return NaN;
  return (ms - now) / 86400000;
}

/**
 * 多行文本 / 数组 → 去空去重的字符串列表（不截断，供校验统计与装配共用）。
 * 表单里 ST / 卖点存多行字符串，模板 / 草稿兼容数组。
 */
export function parseTiktokStringList(raw) {
  const rows = Array.isArray(raw) ? raw : String(raw ?? '').split(/\r?\n/);
  const out = [];
  rows.forEach((row) => {
    const text = String(row ?? '').trim();
    if (text && !out.includes(text)) out.push(text);
  });
  return out;
}

/** 按官方「条数 + 合计字符」双上限硬截断（装配器防御；校验器已先行拦截） */
export function clampTiktokStringList(list, { max = 0, maxTotalChars = 0 } = {}) {
  const out = [];
  let chars = 0;
  (Array.isArray(list) ? list : []).forEach((raw) => {
    const text = String(raw ?? '').trim();
    if (!text) return;
    if (max > 0 && out.length >= max) return;
    if (maxTotalChars > 0 && chars + text.length > maxTotalChars) return;
    out.push(text);
    chars += text.length;
  });
  return out;
}

/** 表单多行文本展示：数组 → 换行拼接；其余原样字符串化（草稿 / 模板兼容两种形态） */
export function tiktokMultilineText(raw) {
  if (Array.isArray(raw)) {
    return raw.map((row) => String(row ?? '').trim()).filter(Boolean).join('\n');
  }
  return String(raw ?? '');
}

/** 列表合计字符数（与官方口径一致：不含分隔符） */
export function tiktokStringListChars(list) {
  return (Array.isArray(list) ? list : []).reduce((sum, item) => sum + String(item ?? '').trim().length, 0);
}

/**
 * Create Product `idempotency_key`：优先 v4 UUID（需安全上下文），否则本地回退生成。
 * 每次真实提交生成一个，**不要**放进 listing_check。
 */
export function createTiktokIdempotencyKey() {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    /* 非安全上下文 / 旧内核：走回退 */
  }
  const hex = () => Math.floor(Math.random() * 0x10000).toString(16).padStart(4, '0');
  return `tk-${Date.now().toString(16)}-${hex()}${hex()}-${hex()}${hex()}`;
}
